<template>
  <q-card flat class="q-pa-sm full-width no-padding-md" style="max-width: 100vw">
    <q-card-section class="row q-pb-xs text-green-vito justify-between items-center">
      <div class="text-h5">{{ $t("title.logAnalysis") }}</div>
      <q-btn
        icon="play_arrow"
        :label="$t('button.runAnalysis')"
        color="green-vito"
        :loading="running"
        @click="runAnalysis"
        dense
        outline
      />
    </q-card-section>

    <q-card-section class="q-pt-xs q-pb-sm">
      <div class="row q-gutter-sm items-end">
        <q-select
          v-model="filterStore"
          :options="storeOptions"
          option-value="id"
          option-label="name"
          emit-value
          map-options
          :label="$t('label.store')"
          clearable
          dense
          outlined
          style="min-width: 180px"
        />
        <q-input
          v-model="filterDateFrom"
          type="date"
          :label="$t('label.from')"
          dense
          outlined
          clearable
          style="width: 155px"
        />
        <q-input
          v-model="filterDateTo"
          type="date"
          :label="$t('label.to')"
          dense
          outlined
          clearable
          style="width: 155px"
        />
        <q-toggle
          v-model="latestOnly"
          :label="$t('label.latestOnly')"
          color="green-vito"
          :disable="!!(filterDateFrom || filterDateTo)"
        />
        <q-btn icon="search" color="green-vito" dense outline @click="applyFilter" />
        <q-btn icon="clear" dense flat color="grey" @click="clearFilter">
          <q-tooltip>{{ $t("tooltip.clearFilterOrder") }}</q-tooltip>
        </q-btn>
      </div>
    </q-card-section>

    <q-card-section class="q-pt-none">
      <q-table
        class="sticky-header-table size-lg"
        dense
        flat
        bordered
        :rows="rows"
        separator="cell"
        :columns="columnsI18n"
        :loading="loading"
        row-key="index"
        virtual-scroll
        :virtual-scroll-item-size="28"
        :virtual-scroll-sticky-size-start="28"
        v-model:pagination="pagination"
        :rows-per-page-options="[0]"
        :sort-method="() => rows"
        @virtual-scroll="onScroll"
        @request="onRequest"
        ref="scroll-table"
      >
        <template v-slot:body-cell-analyzedAt="props">
          <q-td :props="props">
            {{ $moment(props.value).format("DD/MM/YYYY HH:mm:ss") }}
          </q-td>
        </template>

        <template v-slot:body-cell-store="props">
          <q-td :props="props">
            <router-link
              v-if="props.value"
              :to="'/store/' + props.value.id"
              class="vito-link text-bold"
            >{{ props.value.name }}</router-link>
          </q-td>
        </template>

        <template v-slot:body-cell-terminal="props">
          <q-td :props="props">
            {{ props.value ? props.value.name : "" }}
          </q-td>
        </template>

        <template v-slot:body-cell-issueCount="props">
          <q-td :props="props" class="text-center">
            <q-badge :color="props.value === 0 ? 'green-vito' : 'red-vito'">
              {{ props.value }}
            </q-badge>
          </q-td>
        </template>

        <template v-slot:body-cell-actions="props">
          <q-td :props="props">
            <q-btn
              icon="bug_report"
              round
              dense
              :color="props.row.issueCount > 0 ? 'red-vito' : 'grey'"
              @click="openIssues(props.row)"
            >
              <q-tooltip>{{ $t("tooltip.showLog") }}</q-tooltip>
            </q-btn>
          </q-td>
        </template>
      </q-table>
    </q-card-section>
  </q-card>

  <q-dialog v-model="showIssues" ref="IssuesDialog">
    <q-card bordered style="width: 750px; max-width: 95vw">
      <q-card-section class="row text-green-vito no-wrap justify-between items-center">
        <div>
          <div class="text-h6">{{ selectedAnalysis.store?.name }} / {{ selectedAnalysis.terminal?.name }}</div>
          <div class="text-caption text-grey">
            {{ $t("label.analyzedAt") }} : {{ $moment(selectedAnalysis.analyzedAt).format("DD/MM/YYYY HH:mm") }}
          </div>
        </div>
        <q-btn icon="close" flat round dense v-close-popup />
      </q-card-section>

      <q-separator />

      <q-card-section style="max-height: 65vh; overflow-y: auto" class="q-pa-none">
        <div v-if="issuesLoading" class="flex flex-center q-pa-xl">
          <q-spinner color="green-vito" size="2em" />
        </div>

        <div v-else-if="!issues.length" class="text-center q-pa-xl text-grey">
          {{ $t("emptyList") }}
        </div>

        <q-list v-else bordered separator>
          <q-expansion-item
            v-for="issue in issues"
            :key="issue.id"
            :disable="!issue.detail || !issue.detail.length"
            dense
            expand-separator
          >
            <template #header>
              <q-item-section>
                <div class="row items-center q-gutter-x-sm q-mb-xs">
                  <q-badge :color="kindColor(issue.kind)" class="text-caption">{{ issue.kind }}</q-badge>
                  <span v-if="issue.lineNo" class="text-caption text-grey">
                    {{ $t("label.line") }} {{ issue.lineNo }}
                  </span>
                  <span v-if="issue.timestamp" class="text-caption text-grey">{{ issue.timestamp }}</span>
                </div>
                <div class="text-body2 ellipsis">{{ issue.summary }}</div>
              </q-item-section>
            </template>

            <q-card flat>
              <q-card-section class="q-pa-sm bg-grey-1">
                <div
                  v-for="(line, i) in issue.detail"
                  :key="i"
                  class="text-caption text-mono"
                  style="white-space: pre-wrap; word-break: break-all"
                >{{ line }}</div>
              </q-card-section>
            </q-card>
          </q-expansion-item>
        </q-list>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import LogAnalysisApi from "../../apis/LogAnalysis";
import Api from "../../apis/Api";

export default {
  data: () => ({
    rows: [],
    selectedAnalysis: {},
    issues: [],
    showIssues: false,
    issuesLoading: false,
    loading: false,
    running: false,
    storeOptions: [],
    filterStore: null,
    filterDateFrom: null,
    filterDateTo: null,
    latestOnly: true,
    pagination: { sortBy: "analyzedAt", descending: true, rowsPerPage: 0 },
    pageSize: 50,
    nextPage: 1,
    lastPage: 1000,
  }),

  async mounted() {
    try {
      const res = await Api().get("/store");
      this.storeOptions = res.data
        .filter((s) => s.isActive)
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch {
      // store list is optional for filtering
    }
  },

  methods: {
    async onScroll({ to, index }) {
      const startRow = (this.nextPage - 1) * this.pageSize;
      try {
        if (this.loading !== true && this.nextPage <= this.lastPage && (to === index || to === -1)) {
          this.loading = true;
          const res = await LogAnalysisApi.paginate({
            startRow,
            fetchCount: this.pageSize,
            sortBy: this.pagination.sortBy,
            descending: this.pagination.descending,
            storeId: this.filterStore || undefined,
            dateFrom: this.filterDateFrom || undefined,
            dateTo: this.filterDateTo || undefined,
            latestOnly: this.latestOnly,
          });
          this.lastPage = Math.ceil(res.data.count / this.pageSize);
          res.data.items.forEach((row, i) => { row.index = this.rows.length + i; });
          this.rows = this.rows.concat(res.data.items);
          this.nextPage++;
          this.loading = false;
        }
      } catch (error) {
        this.loading = false;
        const message = error.response ? error.response.data.message : error.message;
        this.$q.notify({ type: "negative", message });
      }
    },

    onRequest({ pagination }) {
      this.pagination.sortBy = pagination.sortBy;
      this.pagination.descending = pagination.descending;
      this.resetAndReload();
    },

    applyFilter() {
      this.resetAndReload();
    },

    clearFilter() {
      this.filterStore = null;
      this.filterDateFrom = null;
      this.filterDateTo = null;
      this.latestOnly = true;
      this.resetAndReload();
    },

    resetAndReload() {
      this.rows = [];
      this.nextPage = 1;
      this.lastPage = 1000;
      this.onScroll({ to: -1, index: -1 });
    },

    async runAnalysis() {
      this.running = true;
      try {
        await LogAnalysisApi.run();
        this.$q.notify({ type: "positive", message: this.$t("api.logAnalysis.done") });
        this.resetAndReload();
      } catch (error) {
        const message = error.response ? error.response.data.message : error.message;
        this.$q.notify({ type: "negative", message });
      } finally {
        this.running = false;
      }
    },

    async openIssues(row) {
      this.selectedAnalysis = row;
      this.issues = [];
      this.showIssues = true;
      this.issuesLoading = true;
      try {
        const res = await LogAnalysisApi.getIssues(row.id);
        this.issues = res.data?.issues ?? [];
      } catch (error) {
        const message = error.response ? error.response.data.message : error.message;
        this.$q.notify({ type: "negative", message });
      } finally {
        this.issuesLoading = false;
      }
    },

    kindColor(kind) {
      if (kind === "CRASH" || kind === "CONFIG_ERROR") return "red";
      if (kind === "STUCK_STATUS_LOOP") return "deep-orange";
      if (kind === "UART_NO_RESPONSE") return "orange";
      if (kind === "NETWORK_OUTAGE") return "blue";
      if (kind === "TIMEOUT") return "amber";
      if (kind === "REBOOT") return "purple";
      if (kind && kind.startsWith("HTTP_5")) return "red";
      if (kind && kind.startsWith("HTTP_4")) return "orange";
      return "grey";
    },
  },

  computed: {
    columnsI18n() {
      return [
        { name: "id", label: "ID", field: "id", align: "center", sortable: true },
        { name: "analyzedAt", label: this.$t("label.analyzedAt"), field: "analyzedAt", align: "center", sortable: true },
        { name: "store", label: this.$t("label.store"), field: "store", align: "left", sortable: true },
        { name: "terminal", label: this.$t("label.terminal"), field: "terminal", align: "center", sortable: true },
        { name: "issueCount", label: this.$t("label.issueCount"), field: "issueCount", align: "center", sortable: true },
        { name: "actions", style: "width: 60px" },
      ];
    },
  },
};
</script>
